<template>
  <div>
    <license-form
      :car-details="exchangeCar"
      :car-index="carIndex"
      :read-only="readOnly"
      @car-fetched="handleCarFetched"
      @car-cleared="handleCarCleared"
    />

    <div v-if="hasExchangeCar">
      <form @submit.prevent="submit">
        <app-fieldset title="inruil-auto details" css-class="exchangeCarForm">
          <app-form-field
            :id="`exchangeType-${carIndex}`"
            :title="i18n.t('/ExchangeCar/TypeMargeOrBtw')"
            class="exchangeType"
          >
            <form-radio-button
              :id="`inpMarge-${carIndex}`"
              :title="i18n.t('/ExchangeCar/MargeCar')"
              :disabled="carIndex > 0"
            >
              <input
                :id="`inpMarge-${carIndex}`"
                v-model="localExchangeCar.Type"
                :name="`inpMarge-${carIndex}`"
                value="MARGE"
                type="radio"
                :disabled="carIndex > 0"
                @change="handleDataChanged"
              />
            </form-radio-button>
            <form-radio-button
              :id="`inpBtw-${carIndex}`"
              :title="i18n.t('/ExchangeCar/BtwCar')"
              :disabled="carIndex > 0"
            >
              <input
                :id="`inpBtw-${carIndex}`"
                v-model="localExchangeCar.Type"
                :name="`inpBtw-${carIndex}`"
                type="radio"
                value="VAT"
                :disabled="carIndex > 0"
                @change="handleDataChanged"
              />
            </form-radio-button>
          </app-form-field>

          <app-form-field
            :id="`tradeInValue-${carIndex}`"
            :title="i18n.t('/ExchangeCar/TradeInValue')"
            :show-label="false"
          >
            <price-input
              :id="`tradeInValue-${carIndex}`"
              v-model="tradeInValue"
              :name="`trade-in-value-${carIndex}`"
              :title="i18n.t('/ExchangeCar/TradeInValue')"
              @change="handleDataChangedRecalculate"
            />
          </app-form-field>

          <app-form-field
            :id="`mileage-${carIndex}`"
            :title="i18n.t('/ExchangeCar/Mileage')"
            :show-label="false"
          >
            <form-input
              :id="`mileage-${carIndex}`"
              v-model="mileage"
              :name="`mileage-${carIndex}`"
              type="text"
              :title="i18n.t('/ExchangeCar/Mileage')"
              @change="handleDataChanged"
            />
          </app-form-field>

          <date-picker
            v-if="isVat"
            :id="`exchangeDate-${carIndex}`"
            :min-date="minExchangeDate"
            :date="exchangeDate"
            :title="i18n.t('/ExchangeCar/ExchangeDate')"
            @change-date="handleExchangeDateChange"
          />

          <div v-if="isVat" class="calculateBpm">
            <app-button
              v-if="allowManualBpmCalculation"
              :data-qa="`manual-bpm-calculation-button-${carIndex}`"
              type="button"
              button-type="secundary"
              @click="toggleManualBpmModal(true)"
            >
              {{ i18n.t('/ExchangeCar/ButtonManualBpmCalculation') }}
            </app-button>

            <app-button
              v-else-if="allowAutomaticBpmCalculation"
              :data-qa="`automatic-bpm-calculation-button-${carIndex}`"
              type="submit"
              button-type="cta"
            >
              {{ i18n.t('/ExchangeCar/ButtonBpmCalculation') }}
            </app-button>
          </div>

          <div v-if="isVat" class="bpmResults">
            <loader v-if="bpmIsCalculating" />
            <div v-else>
              <strong>{{ i18n.t('/ExchangeCar/FinancialData') }}</strong>
              <i v-if="bpmIsManuallyCalculated">
                <br />{{ i18n.t('/ExchangeCar/BpmManuallyCalculatedDisclaimer') }}
              </i>
              <dl>
                <dt>{{ i18n.t('/ExchangeCar/TradeInValue') }}</dt>
                <dd>{{ i18n.n(localExchangeCar.Price.Incl, 'currency') }}</dd>
                <dt>{{ i18n.t('/ExchangeCar/BtwValue') }}</dt>
                <dd>{{ i18n.n(localExchangeCar.Price.Btw, 'currency') }}</dd>
                <dt>{{ i18n.t('/ExchangeCar/BpmResidualValue') }}</dt>
                <dd>{{ i18n.n(localExchangeCar.ResidualBpm, 'currency') }}</dd>
                <dt>{{ i18n.t('/ExchangeCar/ExchangeValueExclBtwBpm') }}</dt>
                <dd>{{ i18n.n(localExchangeCar.Price.Excl, 'currency') }}</dd>
              </dl>
            </div>
          </div>

          <app-form-field
            :id="`remark-${carIndex}`"
            :title="i18n.t('/ExchangeCar/Remarks')"
            class="remark"
            :show-label="false"
          >
            <form-text-area
              :id="`remark-${carIndex}`"
              v-model="remark"
              :name="`remark-${carIndex}`"
              :title="i18n.t('/ExchangeCar/Remarks')"
              @change="handleDataChanged"
            />
          </app-form-field>

          <strong class="priceInclResult">
            {{ i18n.n((localExchangeCar.Price.Incl || 0) * -1, 'currency') }}
          </strong>
        </app-fieldset>

        <!-- Collapse button for expanded view -->
        <div v-if="!readOnly && isExpanded" class="collapse-button-container">
          <app-button
            type="button"
            button-type="secundary"
            :data-qa="`exchange-car-show-details-${carIndex}`"
            :show-icon="false"
            @click="$emit('toggle-collapse')"
          >
            Verberg details
          </app-button>
        </div>
      </form>

      <modal
        v-if="showManualBpmModal"
        :title="i18n.t('/ExchangeCar/ModalManualCalculateBpm/ModalTitle')"
        @close="toggleManualBpmModal(false)"
      >
        <manual-bpm-calculation @on-bpm-calculation="onBpmCalculation" />
      </modal>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { useVuelidate } from '@vuelidate/core'
import LicenseForm from '@/components/generic/presentational/organisms/licenseForm/LicenseForm.vue'

import AppFormField from '@/components/generic/presentational/atoms/formField/FormField.vue'
import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import FormRadioButton from '@/components/generic/presentational/atoms/formRadioButton/FormRadioButton.vue'
import PriceInput from '@/components/generic/presentational/atoms/priceInput/PriceInput.vue'
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import FormTextArea from '@/components/generic/presentational/atoms/formTextarea/FormTextarea.vue'
import DatePicker from '@/components/generic/presentational/atoms/datePicker/DatePicker.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import ManualBpmCalculation from '@/components/generic/presentational/organisms/manualBpmCalculation/ManualBpmCalculation.vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    LicenseForm,
    AppFormField,
    AppFieldset,
    FormRadioButton,
    PriceInput,
    FormInput,
    FormTextArea,
    DatePicker,
    AppButton,
    Modal,
    ManualBpmCalculation,
    Loader,
  },
  props: {
    exchangeCar: {
      type: Object,
      required: true,
    },
    carIndex: {
      type: Number,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    isExpanded: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update', 'remove', 'car-fetched', 'toggle-collapse'],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      localExchangeCar: { ...this.exchangeCar },
      tradeInValue: 0,
      mileage: 0,
      remark: '',
      minExchangeDate: moment(),
      exchangeDate: moment(),
      showManualBpmModal: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      bpmIsCalculating: false,
      bpmIsManuallyCalculated: false,
    }
  },
  computed: {
    ...mapGetters({
      exchangeCarType: 'quote/exchangeCarType',
    }),
    hasExchangeCar() {
      return this.localExchangeCar.ModelName != null
    },
    isVat() {
      return this.localExchangeCar.Type == 'VAT'
    },
    /**
     * CST-4327: Determines whether BPM calculation is applicable for this vehicle
     * The section always shows for VAT cars, but the calculate button is hidden
     * when the category definitively exempts the vehicle from BPM.
     * M1  → always applicable.
     * N1  → applicable only when 1b registration date >= 2025-01-01.
     * N2  → never applicable.
     * null/unknown → applicable (backward compatibility).
     */
    isBpmApplicable() {
      const code = this.localExchangeCar.CategoryCode
      if (!code) return true
      switch (code.toUpperCase()) {
        case 'M1':
          return true
        case 'N2':
          return false
        case 'N1': {
          const date1b = this.localExchangeCar.Licenseplate1BRegistration
          if (!date1b) return false
          return new Date(date1b) >= new Date('2025-01-01')
        }
        default:
          return true
      }
    },
    allowManualBpmCalculation() {
      if (this.localExchangeCar.Type != 'VAT') return false
      if (!this.isBpmApplicable) return false
      return (
        this.localExchangeCar.ModelName == 'onbekend' ||
        !this.localExchangeCar.Bpm.Value ||
        this.localExchangeCar.Bpm.Value == 0 ||
        this.bpmIsManuallyCalculated
      )
    },
    allowAutomaticBpmCalculation() {
      return this.isBpmApplicable
    },
  },
  watch: {
    exchangeCar: {
      handler(newVal) {
        this.localExchangeCar = { ...newVal }
        this.tradeInValue = Number(newVal.TradeValue || 0)
        this.remark = newVal.Remark
        this.mileage = newVal.Mileage
        this.bpmIsManuallyCalculated = newVal.ResidualBPMManualCalculation

        if (this.localExchangeCar.ResidualBpm !== undefined) {
          this.bpmIsCalculating = false
        }
      },
      deep: true,
    },
  },
  created() {
    this.tradeInValue = Number(this.exchangeCar.TradeValue || 0)
    this.remark = this.exchangeCar.Remark
    this.mileage = this.exchangeCar.Mileage
    this.bpmIsManuallyCalculated = this.exchangeCar.ResidualBPMManualCalculation
    this.exchangeDate = !!this.exchangeCar.ExchangeDate
      ? moment(this.exchangeCar.ExchangeDate)
      : moment()
  },
  methods: {
    submit() {
      this.bpmIsCalculating = true
      this.emitUpdate()
    },
    toggleManualBpmModal(showModal) {
      this.showManualBpmModal = showModal
    },
    handleDataChanged() {
      this.emitUpdate()
    },
    handleDataChangedRecalculate() {
      this.bpmIsCalculating = true
      this.emitUpdate()
    },
    handleExchangeDateChange(value) {
      if (value == 'INVALID') return

      this.exchangeDate = value
      this.handleDataChangedRecalculate()
    },
    handleCarFetched(carData) {
      this.localExchangeCar = { ...this.localExchangeCar, ...carData }
      this.emitUpdate()
      // Emit car-fetched event to parent to trigger collapse
      this.$emit('car-fetched')
    },
    handleCarCleared() {
      // Reset to empty state but keep the structure
      this.localExchangeCar = this.createEmptyExchangeCar()
      this.tradeInValue = 0
      this.mileage = 0
      this.remark = ''
      this.$emit('remove', this.carIndex)
    },
    emitUpdate() {
      const tradeInValueNumber = parseFloat(this.tradeInValue) || 0

      const updatedCar = {
        Type: this.localExchangeCar.Type,
        LicensePlate: this.localExchangeCar.LicensePlate,
        Vin: this.localExchangeCar.Vin,
        BrandName: this.localExchangeCar.BrandName,
        ModelName: this.localExchangeCar.ModelName,
        Mileage: this.mileage,
        ExchangeDate: this.exchangeDate.toJSON().slice(0, 19),
        ResidualBpm: this.localExchangeCar.ResidualBpm,
        Price: {
          Incl: tradeInValueNumber,
          Excl: this.localExchangeCar.Price.Excl,
          Btw: this.localExchangeCar.Price.Btw,
          BtwRate: this.localExchangeCar.Price.BtwRate,
        },
        AdmissionDate: this.localExchangeCar.FirstRegistrationWorld,
        Bpm: {
          Value: this.localExchangeCar.Bpm.Value,
        },
        TradeValue: tradeInValueNumber,
        Remark: this.remark,
        CommercialVehicle: this.localExchangeCar.CommercialVehicle,
        ResidualBPMManualCalculation: this.bpmIsManuallyCalculated,
        CategoryCode: this.localExchangeCar.CategoryCode,
        VehicleType: this.localExchangeCar.VehicleType,
        Trimline: this.localExchangeCar.Trimline,
        InteriorColor: this.localExchangeCar.InteriorColor,
        ExteriorColor: this.localExchangeCar.ExteriorColor,
        Engine: this.localExchangeCar.Engine,
        MotExpiryDate: this.localExchangeCar.MotExpiryDate,
        Licenseplate1AIssueing: this.localExchangeCar.Licenseplate1AIssueing,
        Licenseplate1BRegistration: this.localExchangeCar.Licenseplate1BRegistration,
        FirstRegistrationNL: this.localExchangeCar.FirstRegistrationNL,
        FirstRegistrationWorld: this.localExchangeCar.FirstRegistrationWorld,
      }

      this.$emit('update', { index: this.carIndex, car: updatedCar })
    },
    onBpmCalculation(data) {
      this.bpmIsCalculating = true
      this.toggleManualBpmModal(false)
      this.tradeInValue = data.tradeInValue
      this.localExchangeCar.AdmissionDate = data.admissionDate
      this.bpmIsManuallyCalculated = true

      this.localExchangeCar.Price = {
        Incl: data.tradeInValue,
      }
      this.localExchangeCar.Bpm = {
        Value: data.originalBpmValue,
      }

      this.handleDataChangedRecalculate()
    },
    createEmptyExchangeCar() {
      return {
        Type: this.exchangeCarType,
        LicensePlate: null,
        Vin: null,
        BrandName: null,
        ModelName: null,
        Mileage: null,
        ExchangeDate: null,
        ResidualBpm: null,
        Price: {
          Incl: null,
          Excl: null,
          Btw: null,
          BtwRate: null,
        },
        AdmissionDate: null,
        Bpm: {
          Value: null,
        },
        TradeValue: null,
        Remark: null,
        CommercialVehicle: null,
        CategoryCode: null,
        VehicleType: '',
        Trimline: null,
        InteriorColor: null,
        ExteriorColor: null,
        Engine: null,
        MotExpiryDate: null,
        Licenseplate1AIssueing: null,
        Licenseplate1BRegistration: null,
        FirstRegistrationNL: null,
        FirstRegistrationWorld: null,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.exchangeCarForm .fields) {
  grid-template-columns: repeat(4, 1fr);
}

.exchangeType {
  grid-column: 1 / span 4;
}

.calculateBpm {
  align-self: center;
  justify-self: center;
}

.remark {
  grid-column: span 4;
  grid-row: 4;
}

.bpmResults {
  grid-row: 3;
  grid-column: span 3;

  dl {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }
}

.priceExclResult {
  grid-row: 5;
  grid-column: 3;
}

.priceInclResult {
  grid-row: 5;
  grid-column: 4;
}

.collapse-button-container {
  margin-top: 1em;
  display: flex;
  justify-content: center;
}
</style>
